import { permissionsForRoles } from '@agentic/contracts';
import {
  AutonomyBadge,
  Badge,
  buttonClass,
  EmptyState,
  Eyebrow,
  KeyValue,
  Section,
  SkeletonRows,
} from '@agentic/ui-kit';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ChevronLeft, Play } from 'lucide-react';
import { agentsQuery } from '../../../app/queries.ts';
import { QueryError } from '../../../app/route-states.tsx';
import { useMe } from '../../../app/use-me.ts';
import { Availability, KillSwitchControl, pauseHelpText } from '../../../features/registry/kill-switch.tsx';

/** Long ids wrap inside their badge instead of pushing the page wider than a phone screen. */
const WRAP_BADGE = 'h-auto! min-h-5.5 max-w-full whitespace-normal! break-all py-0.5';

export const Route = createFileRoute('/_app/registry/agents/$name')({
  component: AgentPage,
});

function AgentPage() {
  const { name } = Route.useParams();
  const q = useQuery(agentsQuery);
  const me = useMe();
  // Review 10: running an agent starts a case, which needs `task.create`.
  const mayStart = permissionsForRoles(me.roles).includes('task.create');
  if (q.isPending) return <SkeletonRows label="Loading agent" />;
  if (q.isError) {
    return (
      <div className="py-2">
        <QueryError error={q.error} />
      </div>
    );
  }
  const agent = q.data.items.find((a) => a.definition.metadata.name === name);
  if (!agent) return <EmptyState title={`Agent ${name} is not registered`} />;
  const { metadata, spec } = agent.definition;
  return (
    <>
      <header data-page-head className="wb-card p-6 md:p-7">
        <Link
          to="/registry"
          className="inline-flex items-center gap-1 text-[13px] text-ink-3 transition-colors hover:text-ink"
        >
          <ChevronLeft className="size-3.5" aria-hidden /> Agents & tools
        </Link>
        <div className="mt-1.5 flex flex-wrap items-end justify-between gap-4">
          <div>
            <Eyebrow>Agent · {metadata.domain}</Eyebrow>
            <h1 className="mt-1 font-mono text-[24px] leading-tight font-semibold tracking-tight">
              {metadata.name}
            </h1>
            {metadata.description && <p className="wb-prose mt-1 text-ink-2">{metadata.description}</p>}
          </div>
          <div className="flex flex-wrap items-start gap-4">
            {spec.capabilities[0] && !agent.killSwitch && mayStart && (
              // 20a: running an agent starts a one-activity case (the New case drawer, capability preselected).
              <Link
                to="/cases"
                search={{ new: spec.capabilities[0].name }}
                className={buttonClass('primary', 'md')}
                data-testid="run-agent"
              >
                <Play className="size-4" aria-hidden /> Run
              </Link>
            )}
            <Availability status={agent.status} killSwitch={agent.killSwitch} reason={agent.reason} />
            <div className="flex flex-col items-start gap-1">
              <KillSwitchControl
                kind="agents"
                name={metadata.name}
                engaged={agent.killSwitch}
                describedBy="agent-pause-help"
              />
              <p id="agent-pause-help" className="max-w-[18rem] text-[12.5px] text-ink-3">
                {pauseHelpText('agents')}
              </p>
            </div>
          </div>
        </div>
      </header>
      <div className="grid grid-cols-1 gap-4 pb-6 xl:grid-cols-2">
        <Section title="Manifest">
          <KeyValue
            items={[
              { label: 'Version', value: <code className="font-mono">{metadata.version}</code> },
              {
                label: 'API version',
                value: <code className="font-mono">{agent.definition.apiVersion}</code>,
              },
              { label: 'Module', value: agent.module },
              { label: 'Business owner', value: metadata.businessOwner },
              { label: 'Technical owner', value: metadata.technicalOwner },
              { label: 'Workflow', value: <code className="font-mono">{spec.workflowType}</code> },
              { label: 'Framework', value: spec.framework },
              { label: 'Autonomy ceiling', value: <AutonomyBadge value={spec.autonomy} /> },
              { label: 'Identity profile', value: <code className="font-mono">{spec.identityProfile}</code> },
              { label: 'Model profile', value: <code className="font-mono">{spec.modelProfile}</code> },
              { label: 'Policy bundle', value: <code className="font-mono">{spec.policyBundle}</code> },
              {
                label: 'Memory',
                value: `${spec.memory.scope}${spec.memory.crossDomainWrites ? ', cross-domain writes' : ''}`,
              },
              {
                label: 'Input schema',
                value: <code className="font-mono text-[12px]">{spec.inputSchemaRef}</code>,
              },
              {
                label: 'Output schema',
                value: <code className="font-mono text-[12px]">{spec.outputSchemaRef}</code>,
              },
              ...(agent.admittedAt ? [{ label: 'Admitted', value: agent.admittedAt }] : []),
            ]}
          />
        </Section>
        <div className="flex flex-col gap-4">
          <Section title="Limits (enforced by the runtime, not the prompt)">
            <KeyValue
              items={[
                {
                  label: 'Model turns',
                  value: <span className="font-mono">{spec.limits.maxModelTurns}</span>,
                },
                { label: 'Tool calls', value: <span className="font-mono">{spec.limits.maxToolCalls}</span> },
                {
                  label: 'Delegation depth',
                  value: <span className="font-mono">{spec.limits.maxDelegationDepth}</span>,
                },
                {
                  label: 'Concurrent children',
                  value: <span className="font-mono">{spec.limits.maxConcurrentChildren}</span>,
                },
                {
                  label: 'Deadline',
                  value: <span className="font-mono">{spec.limits.deadlineSeconds} s</span>,
                },
              ]}
            />
          </Section>
          <Section title="Prompt">
            <KeyValue
              items={[
                { label: 'Version', value: <code className="font-mono">{spec.prompt.version}</code> },
                {
                  label: 'System ref',
                  value: <code className="font-mono text-[12px]">{spec.prompt.systemRef}</code>,
                },
              ]}
            />
          </Section>
          <Section title="Evaluations">
            {spec.evaluations.length === 0 ? (
              <p className="text-[13px] text-bad">No evaluations: the registry keeps this agent disabled.</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {spec.evaluations.map((e) => (
                  <li key={e} className="font-mono text-[12.5px]">
                    {e}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
        <Section title="Capabilities, tools and knowledge" className="xl:col-span-2">
          <KeyValue
            items={[
              {
                label: 'Capabilities',
                value: spec.capabilities.map((c) => `${c.name}@${c.version}`).join(', ') || '—',
              },
              {
                label: 'Tools',
                value: (
                  <div className="flex flex-wrap gap-1">
                    {spec.tools.map((t) => (
                      <Badge key={t} mono className={WRAP_BADGE}>
                        {t}
                      </Badge>
                    ))}
                  </div>
                ),
              },
              {
                label: 'Knowledge scopes',
                value: (
                  <div className="flex flex-wrap gap-1">
                    {spec.knowledgeScopes.map((t) => (
                      <Badge key={t} mono tone="info" className={WRAP_BADGE}>
                        {t}
                      </Badge>
                    ))}
                  </div>
                ),
              },
            ]}
          />
        </Section>
      </div>
    </>
  );
}
